import { uri as URI } from '../utils/config';
import { uuid } from '../utils/uuid';
import axios from 'axios';

const imageRequestDate = new Date().getTime();

const baseImagesPath = (placeId, productId, res) =>
  `${URI.bucket}/${placeId}/${res}/${productId}.jpg?time=${imageRequestDate}`;

const baseMoviesPath = (placeId, productId, res) =>
  `${URI.bucket}/${placeId}/${res}/${productId}.mp4?time=${imageRequestDate}`;

const getDateValue = (date) => {
  const newDate = date ? new Date(date) : new Date();
  return newDate.getTime();
};

const handleTranslations = (object) => {
  const hasNameTranslations = object?.name?.length > 3 && object?.name?.includes('//');
  if (hasNameTranslations) {
    const translations = object.name.split('//');
    const name = translations[0].trim();
    const internationalName = translations[1].trim();
    object.name = name;
    object.intname = internationalName;
  }
  const hasDetailsTranslations = object?.details?.length > 3 && object?.details?.includes('//');
  if (hasDetailsTranslations) {
    const translations = object.details.split('//');
    const details = translations[0].trim();
    const internationalDetails = translations[1].trim();
    object.details = details;
    object.intdetails = internationalDetails;
  }
  const hasDescriptionTranslations =
    object?.description?.length > 3 && object?.description?.includes('//');
  if (hasDescriptionTranslations) {
    const translations = object.description.split('//');
    const description = translations[0].trim();
    const internationalDescription = translations[1].trim();
    object.description = description;
    object.intdescription = internationalDescription;
  }
};

const generateCaching = ({ placeId, categories, toppingsGroups, toppings, products, services }) => {
  const cache = {
    services: {
      services,
      serviceId__service: {},
      serviceId__categories: {},
      serviceId__images: [],
    },
    categories: {
      categoryId__category: {},
      categoryId__images: {},
      categoryId__products: {},
      nestedId__categories: {},
      categoryId__extraProducts: {},
    },
    products: {
      productId__product: {},
    },
  };

  let serviceProcessed = null;
  try {
    for (const service of services) {
      serviceProcessed = service;
      cache.services.serviceId__service[service.id] = service;
    }
  } catch (error) {
    console.error('ERROR ON INITIALIZING SERVICE', serviceProcessed);
  }

  for (const category of categories) {
    try {
      handleTranslations(category);
      cache.categories.categoryId__category[category.id] = category;
      if (category.nested) {
        const index = Math.abs(Number(category.position)) - 1;
        if (!cache.categories.nestedId__categories[category.nested]) {
          cache.categories.nestedId__categories[category.nested] = [];
        }
        cache.categories.nestedId__categories[category.nested][index] = category;
      } else {
        const index = Math.abs(Number(category.position)) - 1;
        if (!cache.services.serviceId__categories[category.service_id]) {
          cache.services.serviceId__categories[category.service_id] = [];
        }
        cache.services.serviceId__categories[category.service_id][index] = category;
      }
    } catch (error) {
      console.error('ERROR ON CATEGORY', category);
      continue;
    }
  }

  for (const product of products) {
    try {
      handleTranslations(product);
      const hasActiveCategory = cache.categories.categoryId__category[product.category_id];
      if (!hasActiveCategory) {
        continue;
      }
      const index = Number(product.position) - 1;
      if (!cache.categories.categoryId__products[product.category_id]) {
        cache.categories.categoryId__products[product.category_id] = [];
      }
      cache.categories.categoryId__products[product.category_id][index] = product;

      if (product.has_movie) {
        product.moviesXL = [baseMoviesPath(placeId, product.id, 'high')];
      }
      product.imagesXL = product.has_image ? [baseImagesPath(placeId, product.id, 'high')] : [];
      product.imagesULTRA = product.has_image ? [baseImagesPath(placeId, product.id, 'ultra')] : [];

      const productCategoryIsNested =
        cache.categories.categoryId__category[product.category_id] &&
        cache.categories.categoryId__category[product.category_id].nested !== null;
      const nestedId = productCategoryIsNested
        ? cache.categories.categoryId__category[product.category_id].nested
        : null;

      if (product.has_image && product.promote === true) {
        if (!cache.categories.categoryId__images[product.category_id]) {
          cache.categories.categoryId__images[product.category_id] = [];
        }
        if (nestedId && !cache.categories.categoryId__images[nestedId]) {
          cache.categories.categoryId__images[nestedId] = [];
        }
        const image = {
          imagesXL: [baseImagesPath(placeId, product.id, 'high')],
        };

        cache.categories.categoryId__images[product.category_id].push(image);
        nestedId && cache.categories.categoryId__images[nestedId].push(image);
        const category = cache.categories.categoryId__category[product.category_id];
        if (!cache.services.serviceId__images[category.service_id]) {
          cache.services.serviceId__images[category.service_id] = [];
        }
        cache.services.serviceId__images[category.service_id].push(image);
      }

      cache.products.productId__product[product.id] = product;

      if (product.extra_cat !== null) {
        for (const extra_category_id of product.extra_cat) {
          if (!cache.categories.categoryId__extraProducts[extra_category_id]) {
            cache.categories.categoryId__extraProducts[extra_category_id] = [];
          }
          cache.categories.categoryId__extraProducts[extra_category_id].push(product);
        }
      }
    } catch (error) {
      console.error('ERROR ON PRODUCT', error);
      continue;
    }
  }

  for (const serviceId in cache.services.serviceId__service) {
    try {
      const hasCategories = Array.isArray(cache.services.serviceId__categories[serviceId]);
      const categories = hasCategories
        ? cache.services.serviceId__categories[serviceId].filter((category) => Boolean(category))
        : undefined;
      if (!categories) continue;
      for (const category of categories) {
        const delimiter = {
          name: cache.categories.categoryId__category[category.id].name,
          intname: cache.categories.categoryId__category[category.id]?.intname,
          id: cache.categories.categoryId__category[category.id].id,
          delimiter: true,
          position: Math.abs(cache.categories.categoryId__category[category.id].position) - 1,
          main: false,
        };

        if (!cache.categories.categoryId__products[category.id]) {
          cache.categories.categoryId__products[category.id] = [];
        }

        if (!category.group) {
          const categoryProducts = [
            delimiter,
            ...cache.categories.categoryId__products[category.id],
            ...(cache.categories.categoryId__extraProducts[category.id] || []),
          ];
          cache.categories.categoryId__products[category.id] = categoryProducts;
        } else {
          const nestedCategories = cache.categories.nestedId__categories[category.id] || [];
          for (const nestedCategory of nestedCategories) {
            if (!nestedCategory?.id) continue;
            if (!cache.categories.categoryId__products[nestedCategory.id]) {
              cache.categories.categoryId__products[nestedCategory.id] = [];
            }

            const nestedDelimiter = {
              name: cache.categories.categoryId__category[nestedCategory.id].name,
              id: cache.categories.categoryId__category[nestedCategory.id].id,
              delimiter: true,
              position:
                Math.abs(cache.categories.categoryId__category[nestedCategory.id].position) - 1,
              main: false,
            };

            cache.categories.categoryId__products[category.id] = [
              ...cache.categories.categoryId__products[category.id],
              nestedDelimiter,
              ...cache.categories.categoryId__products[nestedCategory.id],
              ...(cache.categories.categoryId__extraProducts[nestedCategory.id] || []),
            ];
          }
        }
      }
    } catch (error) {
      console.error('ERROR ON SERVICE_ID TASK 1', serviceId, error);
      continue;
    }
  }

  for (const serviceId in cache.services.serviceId__service) {
    try {
      const categories = cache.services.serviceId__categories[serviceId];
      if (!categories) continue;
      for (const category of categories) {
        if (!category) continue;
        if (category.has_movie) {
          cache.categories.categoryId__category[category.id].moviesXL = [
            baseMoviesPath(placeId, category.id, 'high'),
          ];
        } else if (category.has_image) {
          const image = [baseImagesPath(placeId, category.id, 'high')];
          cache.categories.categoryId__category[category.id].imagesXL = image;
          category.imagesXL = image;
        } else {
          const imageProducts = cache.categories.categoryId__images[category.id];
          if (!imageProducts || imageProducts.length === 0) {
            cache.categories.categoryId__category[category.id].imagesXL = [];
            category.imagesXL = [];
            continue;
          }
          const randomProduct = imageProducts[Math.floor(Math.random() * imageProducts.length)];
          cache.categories.categoryId__category[category.id].imagesXL = randomProduct.imagesXL;
          category.imagesXL = randomProduct.imagesXL;
        }
      }
    } catch (error) {
      console.error('ERROR ON SERVICE_ID TASK 2', serviceId);
      continue;
    }
  }

  for (const service of services) {
    try {
      handleTranslations(service);
      if (service.has_movie) {
        cache.services.serviceId__service[service.id].moviesXL = [
          baseMoviesPath(placeId, service.id, 'high'),
        ];
        continue;
      }

      if (service.has_image) {
        cache.services.serviceId__service[service.id].imagesXL = [
          baseImagesPath(placeId, service.id, 'high'),
        ];
        continue;
      }

      const serviceImages = cache.services.serviceId__images[service.id];
      if (!serviceImages || !Array.isArray(serviceImages) || serviceImages.length === 0) {
        cache.services.serviceId__service[service.id].imagesXL = [];
        continue;
      }

      const randomProduct = serviceImages[Math.floor(Math.random() * serviceImages.length)];
      cache.services.serviceId__service[service.id].imagesXL = randomProduct.imagesXL;
    } catch (error) {
      console.error('ERROR ON SERVICE', service);
      continue;
    }
  }

  return cache;
};

const initializeMenu = async (placeId, userId = uuid(), pointId = 'default', delivery = false) => {
  try {
    const data = await axios.get(
      `${URI.menu}?id=${placeId}&userId=${userId}&pointId=${pointId}&delivery=${delivery}`,
    );
    const {
      status,
      data: { menu: menuData, delivery: deliveryData },
    } = data;

    const hasDeliveryAndNoData = delivery && deliveryData === undefined;
    const noDeliveryModeButNoMenu = !delivery && menuData === undefined;

    if (status !== 200 || hasDeliveryAndNoData || noDeliveryModeButNoMenu) {
      return { place: {}, cache: {}, status };
    }

    const { place, categories, toppingsGroups, toppings, products, services, events } = delivery
      ? deliveryData
      : menuData;

    const cache = generateCaching({
      place,
      categories,
      toppingsGroups,
      toppings,
      products,
      services,
      events,
    });

    return { place, cache, status };
  } catch (error) {
    if (error?.response?.status === 401) {
      return { place: {}, cache: {}, status: 401 };
    }

    return { place: {}, cache: {}, status: 500 };
  }
};

export { initializeMenu };
